// src/pages/settings/PceForm.tsx — formulaire de création/édition d'un PCE (gaz)

import { useState } from "react";
import type { ClientRecord, SiteRecord } from "../../api/settingsApi";
import type { PceRecord } from "../../api/metersApi";
import type { ConfirmModalProps } from "../../components/ConfirmModal";
import { SiteSelect } from "./SiteSelect";

export function PceForm({ initial, sites, clients, existingPces, setModal, onSave, onClose, canEditEngieSource = false }: {
  initial?: PceRecord; sites: SiteRecord[]; clients: ClientRecord[]; existingPces: PceRecord[];
  setModal: (m: ConfirmModalProps | null) => void;
  onSave: (pce: PceRecord) => Promise<void>; onClose: () => void;
  /** "engie" n'est jamais un choix normal du select Source (seule porte d'entrée : EngieImportModal)
   * — verrouille le select si la source courante est déjà "engie" et que l'appelant n'est pas admin. */
  canEditEngieSource?: boolean;
}) {
  const [id,         setId]         = useState(initial?.id ?? "");
  const [codePostal, setCodePostal] = useState(initial?.codePostal ?? "");
  const [siteId,     setSiteId]     = useState(initial?.siteId ?? "");
  const [label,      setLabel]      = useState(initial?.label ?? "");
  // Symétrie avec PdlForm : "— Aucune —" par défaut pour un nouveau PCE (le défaut DB reste
  // "grdf" pour le backfill des PCE existants — cf. CLAUDE.md, "Migration ÉLEC + GAZ vers
  // ts-ingest" — c'est uniquement l'état initial de CE formulaire qui part sur "").
  const [source,     setSource]     = useState(initial?.source ?? "");
  const [saving,     setSaving]     = useState(false);
  const [err,        setErr]        = useState<string|null>(null);
  const isEngieLocked = source === "engie" && !canEditEngieSource;

  const doSave = async (trimmedId: string) => {
    setSaving(true);
    try {
      await onSave({ id: trimmedId, codePostal, siteId, source, label, createdAt: "", updatedAt: "" });
      onClose();
    } catch(e) { setErr((e as Error).message); setSaving(false); }
  };

  const handleSave = async () => {
    if (!id.trim()) { setErr("Le numéro PCE est obligatoire."); return; }
    const trimmedId = id.trim();
    const existing = !initial ? existingPces.find(p=>p.id===trimmedId) : undefined;
    if (existing) {
      const siteName = sites.find(s=>s.id===existing.siteId)?.name ?? "Sans site";
      setModal({
        title: "PCE déjà existant",
        message: `Un PCE "${trimmedId}" existe déjà (site rattaché : ${siteName}). L'enregistrer écrasera ses données actuelles. Continuer ?`,
        confirmLabel: "Écraser", danger: true,
        onCancel: () => setModal(null),
        onConfirm: () => doSave(trimmedId),
      });
      return;
    }
    await doSave(trimmedId);
  };

  return (
    <div style={{display:"flex",flexDirection:"column",flex:1,minHeight:0}}>
      <div style={{overflowY:"auto",padding:"20px 24px",flex:1}}>
        {initial
          ? <div className="mb-16" style={{padding:"8px 12px",background:"#f8f8f8",borderRadius:6,fontSize:13}}>
              PCE : <strong>{initial.id}</strong>
            </div>
          : <div className="mb-16">
              <label className="label">Numéro PCE *</label>
              <input value={id} onChange={e=>{setId(e.target.value);setErr(null);}} className="field" placeholder="ex : 012345678901"/>
            </div>
        }
        <div className="mb-16">
          <label className="label">Source</label>
          <select value={source} onChange={e=>setSource(e.target.value)} className="field" disabled={isEngieLocked}>
            <option value="">— Aucune —</option>
            <option value="grdf">GRDF</option>
            {source === "engie" && <option value="engie">Engie</option>}
          </select>
          {isEngieLocked && (
            <span style={{fontSize:11,color:"var(--text-faint)",display:"block",marginTop:4}}>
              Alimenté par un import Engie — modifiable uniquement par un administrateur.
            </span>
          )}
        </div>
        <div className="mb-16">
          <label className="label">Site</label>
          <SiteSelect value={siteId} onChange={setSiteId} sites={sites} clients={clients} placeholder="— Sans site —"/>
        </div>
        <div className="mb-16">
          <label className="label">Libellé</label>
          <input value={label} onChange={e=>setLabel(e.target.value)} className="field" placeholder="ex : Bâtiment A"/>
        </div>
        <div className="mb-16">
          <label className="label">Code postal</label>
          <input value={codePostal} onChange={e=>setCodePostal(e.target.value)} className="field" placeholder="ex : 75001"/>
        </div>
      </div>
      <div style={{padding:"16px 24px",borderTop:"1px solid #f0f0f0",background:"#fff",flexShrink:0,display:"flex",flexDirection:"column",gap:10}}>
        {err && <div style={{background:"#fdecea",border:"1px solid #f5c6cb",borderRadius:6,padding:"10px 14px",fontSize:13,color:"var(--red)"}}>⚠ {err}</div>}
        <div className="flex gap-10" style={{justifyContent:"flex-end"}}>
          <button onClick={onClose} className="btn btn-ghost">Annuler</button>
          <button onClick={handleSave} disabled={saving} className="btn btn-black btn-lg">{saving?"Enregistrement…":"Enregistrer"}</button>
        </div>
      </div>
    </div>
  );
}
